import { useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { ActionTextBaseProps } from '@components/action-text/action-text';
import { FiftyLogo } from '@components/icons/fifty-logo';
import { IconInfoSmall } from '@components/icons/info-small';
import { InfoBox } from '@components/info-box';
import { LottieAnimation } from '@components/lottie-animation';
import { LeftContentRightBrandedPage } from '@components/page-containers/page-containers';
import { Text } from '@components/text';
import useQueryParams from '@hooks/use-query-params';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export const AuthenticationPageContainer: React.FC = ({ children }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/templates/')) {
    // TODO: Fix this in a better way?
    return <>{children}</>;
  }

  return (
    <LeftContentRightBrandedPage
      branded={
        <BrandedContainer>
          <LogoContainer>
            <FiftyLogo size={144} color="indigo5" />
          </LogoContainer>
          <LottieAnimation animation="journeys-logo" delay={1500} />
        </BrandedContainer>
      }
    >
      <ContentContainer>{children}</ContentContainer>
    </LeftContentRightBrandedPage>
  );
};

const BrandedContainer = styled.div({
  backgroundColor: theme.color.indigo400,
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const LogoContainer = styled.div({
  position: 'absolute',
  top: pxToRem(-16),
  right: pxToRem(40),
});

const ContentContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  '> div': {
    width: pxToRem(448),
    justifyContent: 'center',
  },
});

export const Title: React.FC = ({ children }) => <Text font="heading1">{children}</Text>;

export const Subtitle: React.FC = ({ children }) => (
  <Text font="regular14" color="textSecondary" center>
    {children}
  </Text>
);

export const linkProps: Partial<ActionTextBaseProps> = {
  color: 'textSecondary',
  underline: true,
  font: 'regular14',
  hoverColor: 'textPrimary',
};

const ErrorBox = ({ text }: { text: string }) => {
  return <InfoBox variant="red" text={text} icon={<IconInfoSmall />} />;
};

export const SSOErrors = () => {
  const { t } = useScopedTranslate(locale);
  const error = useQueryParams('error');
  switch (error) {
    case 'missingGoogleToken':
      return <ErrorBox text={t('missingGoogleToken')} />;
    case 'missingGoogleUserInfo':
      return <ErrorBox text={t('missingGoogleUserInfo')} />;
    case 'missingGoogleCode':
      return <ErrorBox text={t('missingGoogleCode')} />;
    case 'noActiveGoogleUser':
      return <ErrorBox text={t('noActiveUser')} />;
    case 'missingMicrosoftToken':
      return <ErrorBox text={t('missingMicrosoftToken')} />;
    case 'missingMicrosoftUserInfo':
      return <ErrorBox text={t('missingMicrosoftUserInfo')} />;
    case 'missingMicrosoftCode':
      return <ErrorBox text={t('missingMicrosoftCode')} />;
    case 'hasMicrosoftEnterprise':
      return <ErrorBox text={t('hasMicrosoftEnterprise')} />;
    case 'noActiveMicrosoftUser':
      return <ErrorBox text={t('noActiveUser')} />;
    case 'existingCompany':
      return <ErrorBox text={t('existingCompany')} />;
    case 'invalidBusinessEmail':
      return <ErrorBox text={t('invalidBusinessEMail')} />;
  }
  return null;
};

const locale = {
  en: {
    missingGoogleToken: 'Failed to obtain token from Google',
    missingGoogleUserInfo: 'Failed to obtain user info from Google',
    missingGoogleCode: 'Authorization code not provided',
    missingMicrosoftToken: 'Failed to obtain token from Microsoft',
    missingMicrosoftUserInfo: 'Failed to obtain user info from Microsoft',
    missingMicrosoftCode: 'Authorization code not provided',
    hasMicrosoftEnterprise: 'Use the enterprise sign in',
    noActiveUser: 'No active user exists for this account',
    existingCompany: 'This email is already registered',
    invalidBusinessEMail: 'This email is not a business email',
  },
};
